import { PerformerRateDto, PhotoVm, PerformerType, SocialLinkDto } from '@app/modules/account-modules/performers.model';

export interface BookingCustomerSocialLinkVm {
  platform: string | number;
  url: string;
}

export interface BookingCustomerPhoneNumberVm {
  code?: string | null;
  number: string;
  hasTelegram?: boolean;
  hasMax?: boolean;
}

export interface IndividualBookingCustomerDetailsVm {
  name?: string | null;
  cityId?: number;
  socialLinks?: BookingCustomerSocialLinkVm[] | null;
  phoneNumber?: BookingCustomerPhoneNumberVm | null;
  image?: PhotoVm | null;
}

export function resolvedIndividualBookingCustomerCityId(
  data: IndividualBookingCustomerDetailsVm
): number {
  const raw = (data as { cityId?: unknown }).cityId;
  if (raw === null || raw === undefined || raw === '') {
    return 0;
  }
  const n =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string'
        ? Number(raw.trim())
        : NaN;
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
}

export interface PhoneNumberDto {
  number: string;
  hasTelegram?: boolean;
  hasMax?: boolean;
}

export interface RepresenterDto {
  email: string;
  name: string;
  lastName: string;
  birthDate?: string;
  cityId?: number;
  phoneNumber: PhoneNumberDto;
}

export interface CreateBookingCustomerDto {
  representer: RepresenterDto;
  password?: string;
  picKey?: string;
  socialLinks?: SocialLinkDto[] | null;
}

export interface AddressDto {
  fullAddress: string;
  description?: string | null;
}

export interface InnInfoResult {
  inn?: string | null;
  isApprovedForRegistration: boolean;
  emails?: string[] | null;
  phoneNumbers?: string[] | null;
  name?: string | null;
  isActive: boolean;
}

export interface FacilityOptionDto {
  level?: string | null;
  notes?: string | null;
}

export interface VenueFacilitiesDto {
  soundEquipment?: FacilityOptionDto | null;
  lightingEquipment?: FacilityOptionDto | null;
  microphones?: FacilityOptionDto | null;
  backline?: FacilityOptionDto | null;
  soundEngineer?: FacilityOptionDto | null;
  lightingEngineer?: FacilityOptionDto | null;
  technicianSupport?: FacilityOptionDto | null;
  hostOrMc?: FacilityOptionDto | null;
  dressingRoom?: FacilityOptionDto | null;
  backstageArea?: FacilityOptionDto | null;
  equipmentStorage?: FacilityOptionDto | null;
  parking?: FacilityOptionDto | null;
  stagePowerAccess?: FacilityOptionDto | null;
  wiFiOnStage?: FacilityOptionDto | null;
  paysTransfer?: FacilityOptionDto | null;
  paysAccommodation?: FacilityOptionDto | null;
  providesFood?: FacilityOptionDto | null;
  providesFee?: FacilityOptionDto | null;
  hasProjectorOrLed?: FacilityOptionDto | null;
  canRecordOrStream?: FacilityOptionDto | null;
  hasMediaStaff?: FacilityOptionDto | null;
  maxAudienceCapacity?: number | null;
  stageAreaSquareMeters?: number | null;
  timeLimit?: string | null;
}

export interface CreateVenueDto {
  representer: RepresenterDto;
  password?: string;
  picKey?: string | null;
  socialLinks?: SocialLinkDto[] | null;
  contacts?: PhoneNumberDto | null;
  venueName?: string | null;
  inn?: string | null;
  format?: string | null;
  description?: string | null;
  venueTypeTag?: string | null;
  preferredGenres?: number[] | null;
  preferredPerformerTypes?: PerformerType[] | null;
  readyToAcceptApplications?: boolean;
  address?: AddressDto | null;
  facilities?: VenueFacilitiesDto | null;
}

export interface UpdateIndividualCustomerProfileDto {
  name?: string | null;
  cityId: number;
  socialLinks?: SocialLinkDto[] | null;
}

export interface AddIndividualCustomerProfileDto {
  name?: string | null;
  cityId: number;
  socialLinks?: SocialLinkDto[] | null;
}

export interface AddIndividualCustomerProfileResult {
  bookingCustomerId: string;
}

export interface AddVenueProfileDto {
  /** Used only in UI forms; not part of AddVenueProfileDto in API */
  cityId?: number;
  /** Form field; mapped to API `contacts` */
  phoneNumber?: PhoneNumberDto;
  contacts?: PhoneNumberDto;
  socialLinks?: SocialLinkDto[] | null;
  venueName?: string | null;
  inn?: string | null;
  format?: string | null;
  description?: string | null;
  venueTypeTag?: string | null;
  preferredGenres?: number[] | null;
  preferredPerformerTypes?: PerformerType[] | null;
  readyToAcceptApplications?: boolean;
  address?: AddressDto | null;
  facilities?: VenueFacilitiesDto | null;
}

export interface AddVenueProfileResult {
  venueId: string;
}

export interface UpdateVenueProfileDto {
  name?: string | null;
  description?: string | null;
  format?: string | null;
  venueTypeTag?: string | null;
  city?: number;
  preferredGenres?: number[] | null;
  preferredPerformerTypes?: PerformerType[] | null;
  socialLinks?: SocialLinkDto[] | null;
  facilities?: VenueFacilitiesDto | null;
  contacts?: PhoneNumberDto | null;
}

export interface UpdateVenueAddressDto {
  address?: AddressDto | null;
}

export interface AddressVm {
  longitude?: number | null;
  latitude?: number | null;
  description?: string | null;
  fullAddress?: string | null;
  metro?: unknown[] | null;
}

export interface FacilityOptionVm {
  level?: string | null;
  notes?: string | null;
}

export interface VenueFacilitiesVm {
  soundEquipment?: FacilityOptionVm | null;
  lightingEquipment?: FacilityOptionVm | null;
  microphones?: FacilityOptionVm | null;
  backline?: FacilityOptionVm | null;
  soundEngineer?: FacilityOptionVm | null;
  lightingEngineer?: FacilityOptionVm | null;
  technicianSupport?: FacilityOptionVm | null;
  hostOrMc?: FacilityOptionVm | null;
  dressingRoom?: FacilityOptionVm | null;
  backstageArea?: FacilityOptionVm | null;
  equipmentStorage?: FacilityOptionVm | null;
  parking?: FacilityOptionVm | null;
  stagePowerAccess?: FacilityOptionVm | null;
  wiFiOnStage?: FacilityOptionVm | null;
  paysTransfer?: FacilityOptionVm | null;
  paysAccommodation?: FacilityOptionVm | null;
  providesFood?: FacilityOptionVm | null;
  providesFee?: FacilityOptionVm | null;
  hasProjectorOrLed?: FacilityOptionVm | null;
  canRecordOrStream?: FacilityOptionVm | null;
  hasMediaStaff?: FacilityOptionVm | null;
  maxAudienceCapacity?: number | null;
  stageAreaSquareMeters?: number | null;
  timeLimit?: string | null;
}

export interface VenueRegistrationDataVm {
  registrationNumber?: string | null;
  name?: string | null;
}

export interface VenueDetailsVm {
  name?: string | null;
  description?: string | null;
  format?: string | null;
  city?: number;
  preferredGenres?: number[] | null;
  preferredPerformerTypes?: PerformerType[] | null;
  socialLinks?: BookingCustomerSocialLinkVm[] | null;
  contacts?: BookingCustomerPhoneNumberVm | null;
  facilities?: VenueFacilitiesVm | null;
  image?: PhotoVm | null;
  photos?: PhotoVm[] | null;
  isApproved?: boolean;
  readyToAcceptApplications?: boolean;
  venueTypeTag?: string | null;
  address?: AddressVm | null;
  registrationData?: VenueRegistrationDataVm | null;
}

export interface VenueListItemVm {
  id?: string;
  name?: string | null;
  city?: number;
  picPath?: string | null;
  availableToReceiveApplications?: boolean;
  venueTypeTag?: string | null;
  preferredGenres?: number[] | null;
  preferredPerformerTypes?: PerformerType[] | null;
}

export interface VenueListQueryParams {
  page?: number;
  size?: number;
  searchText?: string | null;
  preferredGenres?: number[] | null;
  preferredPerformerTypes?: PerformerType[] | null;
  readyToAcceptApplications?: boolean | null;
  venueTypeTag?: string | null;
  cityId?: number | null;
}

export interface PerformerBookingRatesVm {
  pricePerHour?: number | null;
  pricePerSet?: number | null;
  description?: string | null;
}

export interface BookingPerformerListItemVm {
  id?: string;
  name?: string | null;
  picPath?: string | null;
  rates?: PerformerBookingRatesVm | null;
  isAvailable?: boolean;
  availability?: string | null;
  city?: number;
  hasVideo?: boolean;
  hasMusic?: boolean;
  genres?: number[] | null;
  types?: PerformerType[] | null;
  hashTags?: string[] | null;
}

export interface BookingPerformersListQueryParams {
  page?: number;
  size?: number;
  searchText?: string | null;
  ratePerSetMin?: number | null;
  ratePerSetMax?: number | null;
  ratePerHourMin?: number | null;
  ratePerHourMax?: number | null;
  anyRate?: boolean | null;
  types?: PerformerType[] | null;
  genres?: number[] | null;
  cityId?: number | null;
  onlyWithVideo?: boolean | null;
  onlyWithAudio?: boolean | null;
}

export interface BookingCustomerVm {
  id?: string | null;
  name?: string | null;
  picPath?: string | null;
  avatarPath?: string | null;
  image?: PhotoVm | null;
  isVenue?: boolean;
  phoneNumber?: BookingCustomerPhoneNumberVm | null;
}

export interface BookingPerformerVm {
  id?: string;
  name?: string | null;
  picPath?: string | null;
  avatarPath?: string | null;
  image?: PhotoVm | null;
}

export enum PerformerBookingApplicationStatus {
  Pending = 'Pending',
  Accepted = 'Accepted',
  Declined = 'Declined',
  Withdrawn = 'Withdrawn'
}

export interface PerformerApplicationVm {
  requestId?: string;
  performer?: BookingPerformerVm | null;
  customer?: BookingCustomerVm | null;
  message?: string | null;
  createdAt?: string;
  status?: PerformerBookingApplicationStatus | null;
  decisionAt?: string | null;
  performedAt?: string | null;
}

export interface VenueApplicationsQueryParams {
  page?: number;
  size?: number;
  from?: string | null;
  to?: string | null;
  status?: PerformerBookingApplicationStatus | null;
}

export interface AcceptApplicationDto {
  performanceDate?: string | null;
}

export interface SendApplicationDto {
  venueId: string;
  message?: string | null;
}

export interface PerformerBookingValuesVm {
  /** API typo: avialability */
  avialability?: string | null;
  availability?: string | null;
  isAvailable?: boolean;
  rates?: PerformerBookingRatesVm | null;
}

export interface UpdatePerformerAvailabilityDto {
  availability?: string | null;
}

export interface PerformerApplicationsQueryParams {
  page?: number;
  size?: number;
  from?: string | null;
  to?: string | null;
  status?: PerformerBookingApplicationStatus | null;
}

export interface BookingCustomerNotificationsSettingsVm {
  mainBookingNotificationsEnabled?: boolean;
  newPerformersNotificationsEnabled?: boolean;
  performerStateChangesNotificationsEnabled?: boolean;
}

export interface UpdateBookingCustomerNotificationSettingsDto {
  mainBookingNotificationsEnabled?: boolean;
  newPerformersNotificationsEnabled?: boolean;
  performerStateChangesNotificationsEnabled?: boolean;
}

/** GET `/booking/performers/{id}/settings/notifications` */
export interface BookingPerformerNotificationsSettingsVm {
  mainBookingNotificationsEnabled?: boolean;
  newVenueRegistrationNotificationsEnabled?: boolean;
  /** Response field name in GET */
  venuesOnlyWithAppliedBooking?: boolean;
  /** Alias used in PUT body */
  venuesOnlyWithAppliedBookingNotificationsEnabled?: boolean;
}

export interface UpdateBookingPerformerNotificationsSettingsDto {
  mainBookingNotificationsEnabled?: boolean;
  newVenueRegistrationNotificationsEnabled?: boolean;
  venuesOnlyWithAppliedBookingNotificationsEnabled?: boolean;
}

export interface BookingCustomerPhotosDto {
  picKeys: string[];
}

export enum AvailabilityOption {
  No = 'No',
  Yes = 'Yes',
  OnRequest = 'OnRequest'
}

export enum EquipmentAvailability {
  No = 'No',
  Yes = 'Yes',
  Text = 'Text'
}

export interface EquipmentProvisionDto {
  availability: EquipmentAvailability;
  description?: string | null;
}

export interface PerformanceTimeOptionDto {
  isAgreedSeparately: boolean;
  time?: string | null;
}

export interface HonorariumOfferDto {
  isNegotiable: boolean;
  amount?: number | null;
}

export interface PerformanceConditionsDto {
  coversTravel: boolean;
  coversAccommodation: AvailabilityOption;
  providesMeals: AvailabilityOption;
  hasTechnicalStaff: AvailabilityOption;
  equipment: EquipmentProvisionDto;
  performanceTime: PerformanceTimeOptionDto;
  honorarium: HonorariumOfferDto;
}

export interface SendHiringDto {
  performerId: string;
  performanceConditions: PerformanceConditionsDto;
  message?: string | null;
  performanceDate?: string | null;
}

export enum BookingPerformerHiringStatus {
  Pending = 'Pending',
  Accepted = 'Accepted',
  Declined = 'Declined',
  Completed = 'Completed',
  Withdrawn = 'Withdrawn',
  Unsuccessful = 'Unsuccessful'
}

const HIRING_STATUS_BY_API_INDEX: readonly BookingPerformerHiringStatus[] = [
  BookingPerformerHiringStatus.Pending,
  BookingPerformerHiringStatus.Accepted,
  BookingPerformerHiringStatus.Declined,
  BookingPerformerHiringStatus.Completed,
  BookingPerformerHiringStatus.Withdrawn,
  BookingPerformerHiringStatus.Unsuccessful
];

export function coerceHiringStatus(
  raw: unknown
): BookingPerformerHiringStatus | null | undefined {
  if (raw === null) {
    return null;
  }
  if (raw === undefined || raw === '') {
    return undefined;
  }
  if (typeof raw === 'number') {
    if (!Number.isInteger(raw) || raw < 0 || raw >= HIRING_STATUS_BY_API_INDEX.length) {
      return undefined;
    }
    return HIRING_STATUS_BY_API_INDEX[raw];
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (t === '') {
      return undefined;
    }
    if (/^\d+$/.test(t)) {
      const n = Number(t);
      if (n >= 0 && n < HIRING_STATUS_BY_API_INDEX.length) {
        return HIRING_STATUS_BY_API_INDEX[n];
      }
      return undefined;
    }
    const names = Object.values(BookingPerformerHiringStatus) as string[];
    if (names.includes(t)) {
      return t as BookingPerformerHiringStatus;
    }
    return undefined;
  }
  return undefined;
}

export interface PerformerHiringVm {
  id?: string;
  customer?: {
    id?: string;
    name?: string | null;
    picPath?: string | null;
    avatarPath?: string | null;
    image?: {
      path?: string | null;
      thumbnailPath?: string | null;
    } | null;
  } | null;
  performer?: {
    id?: string;
    name?: string | null;
    picPath?: string | null;
    avatarPath?: string | null;
    image?: {
      path?: string | null;
      thumbnailPath?: string | null;
    } | null;
  } | null;
  message?: string | null;
  conditions?: PerformanceConditionsDto | null;
  performanceConditions?: PerformanceConditionsDto | null;
  performanceDate?: string | null;
  status?: BookingPerformerHiringStatus | null;
  createdAt?: string | null;
  decisionAt?: string | null;
  performedAt?: string | null;
}

export function resolveHiringConditions(
  hiring: PerformerHiringVm | null | undefined
): PerformanceConditionsDto | null {
  if (!hiring) {
    return null;
  }
  return hiring.conditions ?? hiring.performanceConditions ?? null;
}

export function normalizePerformerHiringVm(item: PerformerHiringVm): PerformerHiringVm {
  const c = resolveHiringConditions(item);
  const rawStatus = (item as { status?: unknown }).status;
  const status = coerceHiringStatus(rawStatus);

  let base: PerformerHiringVm;
  if (c == null && item.conditions == null && item.performanceConditions == null) {
    base = { ...item };
  } else {
    base = {
      ...item,
      conditions: c,
      performanceConditions: c
    };
  }

  if (status !== undefined) {
    base.status = status;
  } else if (typeof rawStatus === 'number') {
    delete (base as { status?: unknown }).status;
  }

  return base;
}
